import { appConfig, productSearchConfigured } from '../config';

export const HIGHLIGHT_OPEN = '\u0001';
export const HIGHLIGHT_CLOSE = '\u0002';

export interface ProductHit {
  objectID: string;
  rouCode: string;
  productName: string;
  countryCode: string;
  countryName: string;
  kiteProductNumbers: string[];
  safetyDatasheetCount: number;
  locales: string[];
  localeNames: string[];
  /** The same fields with the matched runs wrapped in the sentinels above. */
  highlighted: {
    productName: string;
    rouCode: string;
    kiteProductNumbers: string[];
  };
}

interface AlgoliaHighlight {
  value?: string;
}

interface AlgoliaHit extends Partial<Omit<ProductHit, 'highlighted'>> {
  _highlightResult?: {
    productName?: AlgoliaHighlight;
    rouCode?: AlgoliaHighlight;
    kiteProductNumbers?: AlgoliaHighlight[];
  };
}

const HITS_PER_PAGE = 20;

function normalise(hit: AlgoliaHit): ProductHit {
  const highlight = hit._highlightResult ?? {};
  return {
    objectID: hit.objectID ?? '',
    rouCode: hit.rouCode ?? '',
    productName: hit.productName ?? '',
    countryCode: hit.countryCode ?? '',
    countryName: hit.countryName ?? '',
    kiteProductNumbers: hit.kiteProductNumbers ?? [],
    safetyDatasheetCount: hit.safetyDatasheetCount ?? 0,
    locales: hit.locales ?? [],
    localeNames: hit.localeNames ?? [],
    highlighted: {
      productName: highlight.productName?.value ?? hit.productName ?? '',
      rouCode: highlight.rouCode?.value ?? hit.rouCode ?? '',
      // Mapping an absent highlight array yielded an empty one, which dropped the numbers from the row
      // rather than showing them unhighlighted.
      kiteProductNumbers: highlight.kiteProductNumbers
        ? highlight.kiteProductNumbers.map(
            (entry, index) => entry?.value ?? hit.kiteProductNumbers?.[index] ?? '',
          )
        : (hit.kiteProductNumbers ?? []),
    },
  };
}

export async function searchProducts(
  query: string,
  countryCode: string | null,
  signal?: AbortSignal,
): Promise<ProductHit[]> {
  if (!productSearchConfigured()) {
    return [];
  }

  const params = new URLSearchParams();
  params.set('query', query);
  params.set('hitsPerPage', String(HITS_PER_PAGE));

  // A word nobody indexed used to empty the whole result: "neutrac cleaner" found nothing at all.
  // Dropping trailing words until something matches is what makes the search forgiving.
  params.set('removeWordsIfNoResults', 'lastWords');
  params.set('minWordSizefor1Typo', '3');

  // Codes are identifiers, not prose. With typo tolerance on them a search for 343800 answered with
  // nine products and the one that actually carries that number was not first.
  params.set('disableTypoToleranceOnAttributes', 'rouCode,kiteProductNumbers');

  // Sentinels rather than tags, so the result is split into elements instead of being trusted as HTML.
  params.set('highlightPreTag', HIGHLIGHT_OPEN);
  params.set('highlightPostTag', HIGHLIGHT_CLOSE);

  if (countryCode) {
    params.set('filters', `countryCode:${countryCode}`);
  }

  const response = await fetch(
    `https://${appConfig.productSearchAppId}-dsn.algolia.net/1/indexes/${encodeURIComponent(appConfig.productSearchIndexName)}/query`,
    {
      method: 'POST',
      headers: {
        'X-Algolia-Application-Id': appConfig.productSearchAppId,
        'X-Algolia-API-Key': appConfig.productSearchApiKey,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ params: params.toString() }),
      signal,
    },
  );

  if (!response.ok) {
    throw new Error(`Product search failed with ${response.status}`);
  }

  const payload = (await response.json()) as { hits?: AlgoliaHit[] };
  return (payload.hits ?? []).map(normalise);
}
